<script setup lang="ts">
/**
 * 卡片消息组件
 * - 商品卡片
 * - 订单卡片
 * - 退货卡片
 */
import CardContent from './CardContent.vue';
import { useI18n } from 'vue-i18n';
import {
  CardConfigs,
  CardType,
  getComputedProps,
  parseStringToObject,
  type ICardMessage,
} from './utils';
// @ts-ignore
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { RefundStatus } from './Cards/types';

const RefundStatusMapColor =  {
  [RefundStatus.REFUND_PROCESSING]: '#398DE6',
  [RefundStatus.REFUND_PROCESSED]: '#6FC017',
  [RefundStatus.REFUND_FAILED]: '#FF4040',
  [RefundStatus.OTHER]: '#CCCCCC',
}

const props = defineProps<{
  data: ICardMessage;
}>();

const { t } = useI18n();
const { data } = props;

const { sender, content } = data ?? {};
const contentObject = parseStringToObject(content);
let { cardType, data: messageData } = contentObject ?? {}; // todo...

const { showStatus, showStatusIcon, isRtl } = messageData ?? {};

const { isShowAvatar, isSentByBot, createdAt, isContentMode } =
  getComputedProps(data, cardType);

const { cardContent } = CardConfigs[cardType ?? ''] ?? {};
</script>

<template>
  <div
    class="card-message-wrapper"
    :class="{ 'sent-by-bot': isSentByBot, 'content-mode': isContentMode }"
    :dir="isRtl ? 'rtl' : 'ltr'"
  >
    <div class="card-message-card">
      <p v-if="!contentObject" class="error-message">
        {{ t('CONVERSATION.CARD_MESSAGE.ERROR') }}
      </p>
      <p v-else-if="!cardContent" class="error-message">
        {{ t('CONVERSATION.CARD_MESSAGE.UNKNOWN_CARD_TYPE') }}
      </p>
      <div v-else class="card-message-container flex column">
        <template v-if="cardType === CardType.REVERSE_ORDER">
          <div class="flex justify-between items-center px-0 text-[14px] leading-[28px]">
            <!-- @vue-skip -->
            <span v-if="messageData.afterSalesTypeTxt" class="font-semibold">{{ messageData.afterSalesTypeTxt }}</span>
            <span class="flex items-center">
              <!-- @vue-ignore -->
              <span v-if="messageData.refundStatus" class="inline-block rounded-full w-[8px] h-[8px] me-[6px] bold" :style="{ 'background-color': `${RefundStatusMapColor[messageData.refundStatus]}` }" />
              <span v-if="showStatus">{{ showStatus }}</span>
            </span>
          </div>
        </template>
        <template v-else>
          <div v-if="showStatus" class="card-header flex">
            <div class="card-title">
              <img
                v-if="showStatusIcon"
                :src="showStatusIcon"
                alt="status-icon"
              />
              <span>{{ showStatus }}</span>
            </div>
          </div>
        </template>
        <div class="card-body">
          <CardContent :type="cardType" :message="messageData" />
        </div>
      </div>
      <div v-if="createdAt" class="card-date">
        {{ messageTimestamp(createdAt, 'LLL d, h:mm a') }}
      </div>
    </div>
    <div v-if="sender && isShowAvatar" class="sender-info">
      <woot-thumbnail
        :src="sender.thumbnail || sender.avatar_url"
        :username="sender.name"
        size="16px"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-message-wrapper {
  display: inline-flex;
  align-items: flex-end;
  flex-direction: row-reverse;
  gap: 8px;
}

.card-message-wrapper:not([dir='rtl']) {
  text-align: left !important;
}

.card-message-wrapper[dir='rtl'] {
  text-align: right !important;
}

.sender-info {
  padding-block: 8px 4px;
}

.flex {
  display: flex;
  align-items: center;
}
.column {
  flex-direction: column;
}
</style>

<style lang="scss">
.card-message-card {
  padding: 12px;
  // 卡片固定宽度
  width: 300px;
  border-radius: 10px;
  background-color: #fff;
  color: #000;
  border: 1px solid #f0f0f2;
}

.card-message-container {
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  gap: 10px;

  & > * {
    width: 100%;
  }

  .card-header {
    gap: 10px;

    svg {
      width: 26px;
      height: 26px;
      flex-shrink: 0;
    }

    .card-title {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 16px;
      font-weight: 600;

      img {
        width: 26px;
        height: 26px;
      }
    }

    .card-icon {
      justify-content: center;
      background-color: #f4f4f4;
      border-radius: 50%;
    }
  }

  .action-button {
    background-color: #000;
    color: #fff;
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 14px;
    text-align: center;
    font-weight: 600;
    cursor: pointer;
  }

  .image,
  img:not(.return-mode-img):not(.modal-image) {
    flex-shrink: 0;
    height: 100%;
    aspect-ratio: 3/4;
  }

  * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.content-mode {
  .card-message-card {
    padding-block: 8px;
    border-radius: 0.25rem 0.5rem 0.5rem 0.25rem;
  }
}

.right {
  .content-mode {
    .card-message-card {
      border-radius: 0.5rem 0.25rem 0.25rem 0.5rem;
    }
  }
  .card-message-wrapper {
    flex-direction: row;
  }
}

.sent-by-bot {
  .card-message-card {
    background-color: rgb(101 80 185 / var(--tw-bg-opacity));
    color: rgb(255 255 255 / var(--tw-text-opacity));

    @apply bg-violet-400 dark:bg-violet-400;
  }
}

.card-date {
  margin-top: 8px;
  font-size: 0.625rem;
  color: #b0b3b8;
}

.is-private .card-date {
  @apply text-slate-400 dark:text-slate-400;
}

body.dark {
  .card-message-card {
    border-color: #fff;
  }
}
</style>
